import { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { ScrollPage } from '@/components/ScrollPage';
import { Button } from '@/components/ui/button';
import { Plus, Search, Edit, Trash2, Loader2, Shield, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useRoles,
  useCreateRole,
  useDeleteRole,
  useCheckRoleNameAvailability,
} from '@/hooks/useRoles';
import { useAuth } from '@/contexts/AuthContext';
import { Role, CreateRolePayload } from '@/types';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function Roles() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role?.toLowerCase() === 'admin' || currentUser?.roles?.includes('Admin');

  const { data: roles, isLoading: rolesLoading } = useRoles();
  const createRoleMutation = useCreateRole();
  const deleteRoleMutation = useDeleteRole();

  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingRole, setDeletingRole] = useState<Role | null>(null);

  // Form state
  const [roleName, setRoleName] = useState('');

  // Name availability state
  const debouncedRoleName = useDebouncedValue(roleName, 400);
  const shouldCheckName = debouncedRoleName.trim().length > 0;
  const { data: nameAvailability } = useCheckRoleNameAvailability(debouncedRoleName, shouldCheckName);

  const handleOpenCreateDialog = () => {
    setRoleName('');
    setIsCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setRoleName('');
  };

  const handleCreate = async () => {
    if (!roleName || !nameAvailability?.isAvailable) {
      return;
    }

    const payload: CreateRolePayload = {
      name: roleName.trim(),
    };
    await createRoleMutation.mutateAsync(payload);
    handleCloseCreateDialog();
  };

  const handleOpenDeleteDialog = (role: Role) => {
    setDeletingRole(role);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setDeletingRole(null);
  };

  const handleDelete = async () => {
    if (deletingRole) {
      await deleteRoleMutation.mutateAsync(deletingRole.id);
      handleCloseDeleteDialog();
    }
  };

  // Filter roles by search query
  const filteredRoles =
    roles?.filter((role) =>
      role.name.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

  const isFormValid = roleName.trim().length > 0 && nameAvailability?.isAvailable;

  if (!isAdmin) {
    return (
      <Layout>
        <div className="text-center py-12">
          <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold">Access Denied</h1>
          <p className="text-muted-foreground mt-2">
            You need administrator privileges to access role management.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <ScrollPage
        framed={!rolesLoading && filteredRoles.length > 0}
        footer={
          !rolesLoading && filteredRoles.length > 0 ? (
            <p className="text-sm text-muted-foreground">
              {filteredRoles.length} role{filteredRoles.length === 1 ? '' : 's'}
            </p>
          ) : null
        }
        header={
          <>
        <PageHeader
          title="Roles"
          description="Manage system roles and permissions"
          actions={
            <Button onClick={handleOpenCreateDialog}>
              <Plus className="h-4 w-4" />
              New Role
            </Button>
          }
        />

        <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-base font-semibold">
              All Roles ({filteredRoles.length})
            </h2>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search roles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          </>
        }
      >
          {rolesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Role Name</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRoles.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        className="text-center py-8 text-muted-foreground"
                      >
                        No roles found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRoles.map((role) => (
                      <TableRow key={role.id}>
                        <TableCell className="font-mono text-sm">
                          {role.id}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-primary" />
                            <span className="font-semibold">{role.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleOpenDeleteDialog(role)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </>
          )}
      </ScrollPage>

        {/* Create Role Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={handleCloseCreateDialog}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Create New Role</DialogTitle>
              <DialogDescription>
                Add a new role to the system. Role names should be unique and descriptive.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="roleName">Role Name *</Label>
                <Input
                  id="roleName"
                  placeholder="e.g., Manager, Viewer, Editor"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                />
                {shouldCheckName && nameAvailability && (
                  <div className="flex items-center gap-2 text-sm">
                    {nameAvailability.isAvailable ? (
                      <>
                        <CheckCircle2 className="h-4 w-4 text-success-foreground" />
                        <span className="text-success-foreground">Role name available</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4 text-destructive" />
                        <span className="text-destructive">
                          Role name already exists
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseCreateDialog}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!isFormValid || createRoleMutation.isPending}
              >
                {createRoleMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Create Role
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={handleCloseDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Role</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p>
                    Are you sure you want to delete the role{' '}
                    <strong className="text-foreground">{deletingRole?.name}</strong>?
                  </p>
                  <Alert variant="destructive" className="mt-3">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      If this role is assigned to any users, the deletion will fail.
                      You must reassign those users to different roles first.
                    </AlertDescription>
                  </Alert>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleteRoleMutation.isPending}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteRoleMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Delete Role
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
    </Layout>
  );
}
